// Inline SVG icons ported 1:1 from docs/helpdesk-seo-plan/prototype/lib/templates.js
// (CATEGORY_ICONS) and public/client.js feedback buttons. No emoji anywhere —
// per the prototype's explicit design decision.
import React from 'react';

// Path bodies copied verbatim from templates.js CATEGORY_ICONS.
const CATEGORY_ICON_PATHS: Record<string, string> = {
    rocket:
        '<path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>',
    send: '<path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/>',
    chart: '<path d="M3 3v16a2 2 0 0 0 2 2h16"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/>',
    card: '<rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/>',
    shield:
        '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>',
    code: '<polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>',
    scale:
        '<path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="M7 21h10"/><path d="M12 3v18"/><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"/>',
    lifebuoy:
        '<circle cx="12" cy="12" r="10"/><path d="m4.93 4.93 4.24 4.24"/><path d="m14.83 9.17 4.24-4.24"/><path d="m14.83 14.83 4.24 4.24"/><path d="m9.17 14.83-4.24 4.24"/><circle cx="12" cy="12" r="4"/>',
    book: '<path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20"/>',
};

interface CategoryIconProps {
    name: string;
    size?: number;
    className?: string;
}

/**
 * Renders one of the 9 category icons by key (matches the backend `icon`
 * field: rocket, send, chart, card, shield, code, scale, lifebuoy, book).
 * Falls back to the book icon for unknown keys, same as the prototype.
 */
export const CategoryIcon: React.FC<CategoryIconProps> = ({ name, size = 24, className }) => {
    const body = CATEGORY_ICON_PATHS[name] || CATEGORY_ICON_PATHS.book;
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.9}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={className}
            dangerouslySetInnerHTML={{ __html: body }}
        />
    );
};

/** Chevron used for category-card and top-task "go" affordances. */
export const ChevronIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 12h14" />
        <path d="m12 5 7 7-7 7" />
    </svg>
);

/** Search magnifier used in the hub hero and search page inputs. */
export const SearchIcon: React.FC<{ size?: number; className?: string }> = ({ size = 20, className }) => (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
    </svg>
);

/** "Was this helpful? Yes" thumbs-up icon, copied verbatim from templates.js. */
export const ThumbsUpIcon: React.FC<{ size?: number }> = ({ size = 17 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M7 10v12" />
        <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" />
    </svg>
);

/** "Was this helpful? No" thumbs-down icon, copied verbatim from templates.js. */
export const ThumbsDownIcon: React.FC<{ size?: number }> = ({ size = 17 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17 14V2" />
        <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" />
    </svg>
);

/** Branded TrafficCreator logo mark used in the header, copied from templates.js logoMark(). */
export const HelpDeskLogoMark: React.FC = () => (
    <span className="hc-brand" aria-hidden="true">
        <svg width="30" height="30" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <linearGradient id="hc-tcg" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ff4d00" />
                    <stop offset="100%" stopColor="#ff8c00" />
                </linearGradient>
            </defs>
            <rect width="28" height="28" x="2" y="2" rx="6" fill="#111" />
            <path d="M10 8h12v4h-4v12h-4V12h-4V8z" fill="url(#hc-tcg)" />
            <path d="M22 18l4 4h-8l4-4z" fill="#ff4d00" />
            <circle cx="26" cy="6" r="3" fill="#ff4d00" />
        </svg>
    </span>
);
